import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Input } from '../components/ui/input';
import {
  Send, Mail, Phone, MessageSquare, Loader2, Search,
  CheckCircle2, Clock, ArrowRight, Sparkles, Inbox,
  Calendar, ChevronRight, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Outreach = () => {
  const [feed, setFeed] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | email | call | message
  const [search, setSearch] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [f, s] = await Promise.all([
        axios.get(`${API_URL}/outreach/feed?limit=80`),
        axios.get(`${API_URL}/outreach/stats`),
      ]);
      setFeed(f.data.items || []);
      setStats(s.data || {});
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return feed.filter(item => {
      if (filter !== 'all' && (item.contact_type || '') !== filter) return false;
      if (search) {
        const q = search.toLowerCase();
        const blob = `${item.lead?.business_name || ''} ${item.subject || ''} ${item.content || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [feed, filter, search]);

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-8 space-y-6 lg:space-y-8 max-w-[1500px] mx-auto" data-testid="outreach-content">

        {/* Hero */}
        <section className="card-base p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute right-0 top-0 w-72 h-72 bg-gradient-to-br from-violet-200/40 to-blue-200/40 rounded-full blur-3xl -mr-20 -mt-20" />
          <div className="relative grid sm:grid-cols-[1fr_auto] gap-4 items-end">
            <div>
              <p className="text-[11px] font-bold tracking-[0.18em] text-violet-700 uppercase mb-2">Outreach</p>
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900">Opportunities Created</h1>
              <p className="text-slate-600 mt-2 max-w-xl">
                Every email, call, and message you've sent — tracked and ready for the next move.
              </p>
            </div>
            <Link to="/leads">
              <button className="btn-primary" data-testid="open-leads-btn">
                <Send className="w-4 h-4" /> Send a new message
              </button>
            </Link>
          </div>
        </section>

        {/* Stats */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Send}        label="Opportunities created" value={stats.messages_sent || 0}
                hint={`+${stats.messages_sent_today || 0} today`} accent="violet" testid="stat-sent" />
          <Stat icon={CheckCircle2} label="Reply rate"             value={`${stats.reply_rate || 0}%`}
                hint={`${stats.replied || 0} replied`} accent="emerald" testid="stat-reply-rate" />
          <Stat icon={Inbox}       label="Replied"                  value={stats.replied || 0}
                hint="Hot conversations" accent="blue" testid="stat-replied" />
          <Stat icon={Clock}       label="Follow-ups due"           value={stats.follow_ups_due || 0}
                hint="Don't lose them" accent="amber" testid="stat-followups" />
        </section>

        {/* Filter bar */}
        <section className="card-base p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search messages, businesses, or subjects…"
                className="pl-9 h-10"
                data-testid="outreach-search"
              />
            </div>
            <div className="flex gap-1 bg-slate-100 p-1 rounded-lg overflow-x-auto">
              {[
                { v: 'all',     l: 'All' },
                { v: 'email',   l: 'Email' },
                { v: 'call',    l: 'Call' },
                { v: 'message', l: 'Message' },
              ].map(f => (
                <button
                  key={f.v}
                  onClick={() => setFilter(f.v)}
                  data-testid={`filter-${f.v}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors whitespace-nowrap ${
                    filter === f.v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-900'
                  }`}
                >
                  {f.l}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Feed */}
        <section>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyOutreach hasAny={feed.length > 0} />
          ) : (
            <div className="space-y-3">
              {filtered.map((item) => (
                <OutreachRow key={item.id || `${item.lead_id}-${item.created_at}`} item={item} />
              ))}
            </div>
          )}
        </section>

        {/* AI hint card */}
        <section className="card-gradient-border">
          <div className="flex flex-col sm:flex-row gap-4 p-5 sm:p-6 items-start sm:items-center">
            <span className="icon-halo flex-shrink-0">
              <Sparkles className="w-4 h-4 text-violet-700" strokeWidth={2.5} />
            </span>
            <div className="flex-1">
              <p className="font-heading font-bold text-slate-900">Need a script that converts?</p>
              <p className="text-sm text-slate-600 mt-1">
                Generate AI emails or call scripts tailored to each potential client.
              </p>
            </div>
            <Link to="/ai-tools">
              <button className="btn-secondary" data-testid="open-ai-tools-btn">
                Open AI tools <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

// ===== Subcomponents =====

const Stat = ({ icon: Icon, label, value, hint, accent, testid }) => {
  const accents = {
    violet: 'from-violet-500 to-violet-600',
    blue: 'from-blue-500 to-blue-600',
    emerald: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-orange-500',
  };
  return (
    <div className="card-base card-hover p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold tracking-[0.16em] text-slate-500 uppercase">{label}</p>
        <span className={`w-9 h-9 rounded-xl bg-gradient-to-br ${accents[accent]} flex items-center justify-center text-white shadow-sm`}>
          <Icon className="w-4 h-4" strokeWidth={2.5} />
        </span>
      </div>
      <p className="text-3xl font-heading font-extrabold text-slate-900 mt-3 tracking-tight">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{hint}</p>
    </div>
  );
};

const OutreachRow = ({ item }) => {
  const lead = item.lead;
  const TypeIcon = item.contact_type === 'call' ? Phone : item.contact_type === 'message' ? MessageSquare : Mail;
  const typeLabel = (item.contact_type || 'message').toUpperCase();
  const created = item.created_at ? new Date(item.created_at) : null;
  const ago = created ? timeAgo(created) : '';

  return (
    <div className="card-base card-hover p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] gap-4 items-start">
      {/* avatar + type */}
      <div className="flex sm:flex-col items-center gap-3 sm:gap-2">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-100 to-blue-100 flex items-center justify-center text-violet-700 font-bold text-sm">
          {(lead?.business_name || 'L').slice(0, 2).toUpperCase()}
        </div>
        <span className="badge-info text-[10px] flex items-center gap-1">
          <TypeIcon className="w-3 h-3" /> {typeLabel}
        </span>
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-heading font-bold text-slate-900 truncate">
            {lead?.business_name || 'Unknown business'}
          </p>
          {lead?.status && <StatusBadge status={lead.status} />}
        </div>
        {lead && (
          <p className="text-xs text-slate-500 mt-0.5">
            {lead.niche || ''}{lead.city ? ` · ${lead.city}` : ''}{lead.state ? `, ${lead.state}` : ''}
          </p>
        )}
        {item.subject && (
          <p className="text-sm font-semibold text-slate-700 mt-2 truncate">{item.subject}</p>
        )}
        {item.content && (
          <p className="text-sm text-slate-600 mt-1 line-clamp-2 whitespace-pre-wrap">{item.content}</p>
        )}
      </div>

      <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:gap-1 text-right flex-shrink-0">
        <span className="text-[11px] text-slate-500 inline-flex items-center gap-1">
          <Calendar className="w-3 h-3" /> {ago}
        </span>
        {lead && (
          <Link to={`/leads/${lead.id}`}
                className="text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-0.5">
            Open <ChevronRight className="w-3 h-3" />
          </Link>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const map = {
    new:       { c: 'badge-info',    l: 'New' },
    contacted: { c: 'badge-warning', l: 'Contacted' },
    follow_up: { c: 'badge-warning', l: 'Follow-up' },
    replied:   { c: 'badge-success', l: 'Replied' },
    closed:    { c: 'badge-success', l: 'Closed' },
  };
  const v = map[status] || map.new;
  return <span className={v.c}>{v.l}</span>;
};

const EmptyOutreach = ({ hasAny }) => (
  <div className="empty-state">
    <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center mb-3 shadow-glow">
      {hasAny ? <AlertCircle className="w-5 h-5 text-white" /> : <Send className="w-5 h-5 text-white" />}
    </span>
    <p className="font-heading font-bold text-slate-900 text-lg">
      {hasAny ? 'No matches for that filter' : 'No outreach yet'}
    </p>
    <p className="text-sm text-slate-500 max-w-sm mt-1.5">
      {hasAny
        ? 'Try clearing the search or filter to see all activity.'
        : "Open a potential client and log your first email, call, or message — every touch counts toward today's outreach quota."}
    </p>
    {!hasAny && (
      <Link to="/leads" className="mt-5">
        <button className="btn-primary" data-testid="empty-leads-btn">
          <Send className="w-4 h-4" /> Open potential clients
        </button>
      </Link>
    )}
  </div>
);

function timeAgo(date) {
  const diff = (Date.now() - date.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString();
}

export default Outreach;
